import { createStore } from 'vuex'
import axios from 'axios'
import * as Config from "../config"
import Cookies from "js-cookie"
import moment from 'moment'

// Configuración global de Axios
axios.defaults.headers.common["Authorization"] = "Bearer " + localStorage.getItem("access_token")
axios.defaults.timeout = 30000 // Aumentar timeout a 30 segundos

// Interceptor para manejar errores de red
axios.interceptors.response.use(
  response => response,
  error => {
    // Manejar errores de timeout y red
    if (error.code === 'ECONNABORTED' || !error.response) {
      console.error('Error de conexión o timeout:', error.message);
      // Actualizar el estado online si corresponde
      if (typeof store !== 'undefined' && store.dispatch) {
        store.dispatch('setOnlineStatus', false);
      }
    }
    return Promise.reject(error);
  }
)

// Crear la instancia del store
const store = createStore({

  state: {
    // Estado de la aplicación
    online: navigator.onLine, // Estado de conexión a Internet
    lastOnlineCheck: Date.now(), // Última vez que se verificó la conexión
    notifications: [], // Lista de notificaciones
    isLoading: false, // Estado de carga
    error: null // Error en la solicitud
  },
  actions: {
    // Acción para obtener notificaciones
    async fetchNotifications({ commit }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        // Conectar a la API para obtener notificaciones
        console.log('Fetching notifications from API...');
        const response = await axios.get(Config.apiPath + '/app/notification/all');
        
        console.log('API Response:', response.data);
        
        if (response.data && response.data.code === 200) {
          // Verificar la estructura de la respuesta
          let notifications = [];
          
          if (response.data.notifications) {
            notifications = response.data.notifications;
          } else if (response.data.data) {
            notifications = response.data.data;
          } else if (Array.isArray(response.data)) {
            notifications = response.data;
          }
          
          console.log('Processed notifications:', notifications);
          commit('SET_NOTIFICATIONS', notifications);
        } else {
          // Si no hay datos o hay un error, establecer un array vacío
          console.log('No notifications found or error in response');
          commit('SET_NOTIFICATIONS', []);
        }
      } catch (error) {
        console.error('Error al cargar notificaciones:', error);
        commit('SET_ERROR', error.message || 'Error al cargar notificaciones');
        commit('SET_NOTIFICATIONS', []);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    // Acción para marcar una notificación como leída
    async markNotificationAsRead({ commit }, id) {
      try {
        // Conectar a la API para marcar notificación como leída
        const response = await axios.put(Config.apiPath + `/app/notification/${id}/read`);
        
        if (response.data && response.data.code === 200) {
          // Actualizar el estado local
          commit('UPDATE_NOTIFICATION', { 
            id, 
            changes: { read: true } 
          });
          
          return true;
        } else {
          console.error('Error en respuesta al marcar notificación como leída:', response.data);
          return false;
        }
      } catch (error) {
        console.error('Error al marcar notificación como leída:', error);
        return false;
      }
    },
    login({ commit }, form) {
      return new Promise((resolve, reject) => {
        // Aumentar el retraso para mejorar la compatibilidad con iOS
        setTimeout(() => {
          axios.post(Config.apiPath + "/auth/login_app", form)
            .then(response => {
              if (response.data.code === 200) {
                console.log('Login exitoso:', response.data.data)
                const accessToken = response.data.data.access_token.token
                const expires_in = moment().format("YYYY-MM-DD HH:mm")
                const expires_at = moment(expires_in).add(4, 'hours').format("YYYY-MM-DD HH:mm")
                const expires_at_date = new Date(expires_at)

                try {
                  // Primero guardar en localStorage para persistencia
                  // Usar try-catch específico para localStorage
                  try {
                    localStorage.setItem("access_token", accessToken)
                    localStorage.setItem("id", response.data.data.id)
                    localStorage.setItem("email", response.data.data.email)
                    localStorage.setItem("expires_at", expires_at)
                    console.log('Datos guardados en localStorage correctamente')
                  } catch (localStorageError) {
                    console.error('Error al guardar en localStorage:', localStorageError)
                  }
                  
                  // Luego guardar en cookies con manejo de errores para iOS
                  try {
                    // Usar configuración más compatible con iOS
                    // Configuración específica para iOS
                    const cookieOptions = { 
                      expires: expires_at_date, 
                      sameSite: 'lax',
                      secure: window.location.protocol === 'https:' // Solo secure en HTTPS
                    }
                    
                    Cookies.set("access_token", accessToken, cookieOptions)
                    Cookies.set("id", response.data.data.id, cookieOptions)
                    Cookies.set("email", response.data.data.email, cookieOptions)
                    Cookies.set("full_name", response.data.data.full_name, cookieOptions)
                    console.log('Cookies guardadas correctamente')
                  } catch (cookieError) {
                    console.warn('Error al guardar cookies, continuando con localStorage:', cookieError)
                  }
                  
                  // Actualizar headers de Axios para futuras peticiones
                  axios.defaults.headers.common["Authorization"] = "Bearer " + accessToken
                  console.log('Headers de Axios actualizados con el token')
                  
                  // Forzar una espera adicional antes de resolver la promesa
                  // para asegurar que los datos se guarden correctamente en iOS
                  setTimeout(() => {
                    console.log('Resolviendo promesa de login después de espera')
                    resolve(response.data)
                  }, 300)
                } catch (storageError) {
                  console.error('Error general al guardar datos de sesión:', storageError)
                  // Intentar continuar a pesar del error
                  setTimeout(() => {
                    resolve(response.data)
                  }, 300)
                }
              } else {
                commit("LOGIN_FAIL1")
                reject(response.data)
              }
            })
            .catch(error => {
              console.error('Error en login:', error)
              reject(error)
            })
        }, 500) // Aumentar retraso para mejorar compatibilidad con iOS
      })
    },
    
    logout() {
      // Limpiar cookies
      const cookies = Cookies.get()
      for (const cookie in cookies) {
        Cookies.remove(cookie)
      }
      
      // Limpiar localStorage
      localStorage.removeItem("access_token")
      localStorage.removeItem("id")
      localStorage.removeItem("email")
      localStorage.removeItem("expires_at")
    },

    getData({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.get(Config.apiPath + "/" + form.url, { params: form.params })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    getDataLite({ commit }, form) {
      return new Promise((resolve, reject) => {
        // Verificar conexión antes de hacer la petición
        if (!navigator.onLine) {
          commit('SET_ONLINE_STATUS', false);
          return reject({ error: 'Sin conexión a Internet' });
        }
        
        axios.get(Config.apiPath + "/" + form.url, {
          // Configuración específica para esta petición
          timeout: 30000 // 30 segundos de timeout
        })
          .then(response => {
            commit('SET_ONLINE_STATUS', true); // Actualizar estado online
            resolve(response.data);
          })
          .catch(error => {
            console.error('Error en getDataLite:', error);
            // Si es un error de red o timeout
            if (error.code === 'ECONNABORTED' || !error.response) {
              commit('SET_ONLINE_STATUS', false);
              reject({ error: 'Tiempo de espera agotado o error de red' });
            } else {
              // Otros errores con respuesta del servidor
              reject(error.response ? error.response.data : { error: error.message });
            }
          })
      })
    },

    createData({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.post(Config.apiPath + "/" + form.url, form.data)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    updateData({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.put(Config.apiPath + "/" + form.url + '/' + form.id, form.data)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    updateDataClean({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.put(Config.apiPath + "/" + form.url, form.data, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${Cookies.get('access_token')}`
          }
        })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    changeStatusData({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.put(Config.apiPath + "/" + form.url + "/" + form.id + "/" + form.status, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${Cookies.get('access_token')}`
          }
        })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    // Actualizar el estado de conexión a Internet
    setOnlineStatus({ commit }, status) {
      commit('SET_ONLINE_STATUS', status);
    },

    getFile({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.get(Config.apiPath + "/upload/all?active=1&type=" + form.type + "&page=" + form.page, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${Cookies.get('access_token')}`
          }
        })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    UploadFile({ commit }, form) {
      return new Promise((resolve, reject) => {
        axios.post(Config.apiPath + "/upload/file", form, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${Cookies.get('access_token')}`
          }
        })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    }
  },
  mutations: {
    // Mutaciones para actualizar el estado
    SET_ONLINE_STATUS(state, status) {
      state.online = status;
      state.lastOnlineCheck = Date.now();
    },
    // Mutaciones para notificaciones
    SET_LOADING(state, status) {
      state.isLoading = status;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_NOTIFICATIONS(state, notifications) {
      state.notifications = notifications;
    },
    UPDATE_NOTIFICATION(state, { id, changes }) {
      state.notifications = state.notifications.map(notification => 
        notification.id === id ? { ...notification, ...changes } : notification
      );
    }
  },
  getters: {
    // Retorna si estamos en línea
    isOnline: state => state.online,
    
    // Getters para notificaciones
    getNotifications: state => state.notifications,
    isLoading: state => state.isLoading,
    getError: state => state.error
  }
})

// Exportar la instancia del store
export default store
